import { getCoreUtils } from "../../../Admin/kReusableCoreTS/kCoreHandler";
import { kHTMLCore } from "../../../Admin/kReusableCoreTS/kHTMLCore";
import { AppActivity, AppActivityRules } from "./AppActivity";
import { AppAvatarRules } from "./AppAvatars";
import { AppIndex } from "./AppIndex";
import { AppOverview, AppOverviewEntry } from "./AppOverview";
import { AppViews } from "./AppViews";
import { AppUpdateEvery } from "./AppUpdateEvery";

// AppOverviewView - the Overview tab as markup: "Most in touch" (a ranked bar list, 30 or 90 days),
// "Gone quiet" and "Replies you owe". Strings only - no DOM, no script: the 30/90 switch is two radio
// inputs and CSS (`app.css` `.ovtop`), so nothing has to be wired after a repaint.
//
// ⭐ THE CHART (dataviz skill, 2026-09-27): one series, so no legend - the heading names it; bars are
// thin, start at one baseline and end in a 4px round; the NUMBER is text in ink beside each bar (never
// the bar colour), and every row is a real link to the chat with a `title` saying the whole sentence.
// ⛔ NO `style=` (the app's CSP is `style-src 'self' blob:`, 2026-09-27): the bar is an SVG `<rect>` whose
// `width="N%"` is a presentation ATTRIBUTE, which the policy does not govern; its fill is `app.css`'s `.ovfill`.
// The bar itself is `aria-hidden` - the name and the number are the accessible content.
// ⛔ Every name is escaped (`kHTMLCore`); every href is the index's own, encoded for the route.

export class AppOverviewView {
    public static readonly Id: string = "wac-overview";
    public static readonly SayId: string = "wac-ovsay";
    /** How every number here is counted - a tooltip and a screen-reader description, never a visible line. */
    public static readonly Counted: string = "Counted from every message in your history - exact, no AI.";
    public static readonly Tip: string = " title=\"" + AppOverviewView.Counted + "\"";

    /** The whole tab under the tabs. */
    public static Tab = (Index: AppIndex, Today: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (AppOverview.HasNumbers(Index) === false) {
                return "<section class=\"ov\" id=\"" + AppOverviewView.Id + "\"><p class=\"none\">The overview "
                    + "appears with the next update (" + AppUpdateEvery.Within + ").</p></section>";
            }
            // ⭐ THE "HOW IT IS COUNTED" SENTENCE IS A TOOLTIP ON EACH HEADING + A SCREEN-READER DESCRIPTION
            // (owner, 2026-09-28: explanations belong in a tooltip, not a line of the screen).
            return "<section class=\"ov\" id=\"" + AppOverviewView.Id + "\" aria-describedby=\"" + AppOverviewView.SayId + "\">"
                + "<p class=\"sr\" id=\"" + AppOverviewView.SayId + "\">" + AppOverviewView.Counted + "</p>"
                + AppOverviewView.TopSection(Index) + AppOverviewView.QuietSection(Index)
                + AppOverviewView.OwedSection(Index, Today) + "</section>";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppOverviewView.Tab");
            return "";
        }
    };

    /** "Most in touch": the heading (with its period) and the ranked bars over the last `AppOverview.Days`. */
    private static TopSection = (Index: AppIndex): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Days: number = AppOverview.Days;
            const Out: string[] = ["<section class=\"ovsec ovtop\" aria-labelledby=\"wac-ovtop-h\">"];
            Out.push("<div class=\"ovhead\"><h2 id=\"wac-ovtop-h\"" + AppOverviewView.Tip + ">Most in touch</h2><span class=\"ovper\">"
                + "Last " + String(Days) + " days</span></div>");
            const Top: AppOverviewEntry[] = AppOverview.Top(Index);
            // ⭐ Two forms, ONE section: the ranked bars on a phone (approved 2026-11-01), the cards in the panes (>= 1100px,
            // `app.css` `wac-review2-work` shows one and hides the other).
            Out.push(AppOverviewView.Bars(Top, Days));
            Out.push(AppOverviewView.Cards(Top, Days));
            Out.push("</section>");
            return Out.join("");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppOverviewView.TopSection");
            return "";
        }
    };

    /** One window's ranked bars, or a sentence when nobody wrote in it. */
    private static Bars = (Rows: AppOverviewEntry[], Days: number): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Cls: string = "ovbars";
            if (Rows.length === 0) {
                return "<p class=\"none " + Cls + "\">No messages in the last " + String(Days) + " days.</p>";
            }
            const Most: number = AppOverview.MaxOf(Rows);
            const Out: string[] = ["<ol class=\"" + Cls + "\">"];
            for (const One of Rows) { Out.push(AppOverviewView.Bar(One, Most, Days)); }
            Out.push("</ol>");
            return Out.join("");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppOverviewView.Bars");
            return "";
        }
    };

    private static Bar = (One: AppOverviewEntry, Most: number, Days: number): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Count: string = One.Count.toLocaleString();
            let Unit: string = " messages";
            if (One.Count === 1) { Unit = " message"; }
            const Tip: string = One.Contact.Title + ": " + Count + Unit + " in the last " + String(Days) + " days";
            // ⚠ At least 2%: a contact with 1 message beside one with 900 still shows a mark.
            const Width: number = Math.max(2, Math.round(One.Count / Most * 100));
            return "<li><a href=\"" + AppOverviewView.Link(One) + "\" title=\"" + kHTMLCore.EscapeAttr(Tip) + "\">"
                + AppOverviewView.Name(One) + "<svg class=\"ovbar\" aria-hidden=\"true\" focusable=\"false\"><rect class=\"ovfill\""
                + " width=\"" + String(Width) + "%\" height=\"8\" rx=\"4\"></rect></svg><span class=\"ovnum\">" + Count
                + "<span class=\"sr\">" + Unit + "</span></span></a></li>";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppOverviewView.Bar");
            return "";
        }
    };

    /**
     * ⭐ "MOST IN TOUCH" ON A WIDE SCREEN (owner review 2026-11-01: "the bar diagram is boring"): avatar-led ranked cards -
     * the contact's disc, the exact count, and a small trend line of messages PER MONTH over three windows the index holds
     * (`Trend`: the six months before the last one, two-three months ago, the last 30 days). Hidden below 1100px.
     * ⛔ No `style=`: the line is an SVG `<polyline points>` (a presentation attribute); its colour is `app.css`'s `.ovspark`.
     */
    private static Cards = (Rows: AppOverviewEntry[], Days: number): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (Rows.length === 0) { return ""; }
            const Out: string[] = ["<ol class=\"ovcards\">"];
            for (let I: number = 0; I < Rows.length; I++) { Out.push(AppOverviewView.Card(Rows[I], I + 1, Days)); }
            Out.push("</ol>");
            return Out.join("");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppOverviewView.Cards");
            return "";
        }
    };

    private static Card = (One: AppOverviewEntry, Rank: number, Days: number): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Count: string = One.Count.toLocaleString();
            const Per: number[] = AppOverviewView.Trend(One.Contact.Act);
            const Tip: string = One.Contact.Title + ": " + Count + " messages in the last " + String(Days) + " days. Per month: "
                + String(Per[0]) + " before, " + String(Per[1]) + " two to three months ago, " + String(Per[2]) + " in the last 30 days";
            return "<li><a class=\"ovcard\" href=\"" + AppOverviewView.Link(One) + "\" title=\"" + kHTMLCore.EscapeAttr(Tip) + "\">"
                + "<span class=\"ovrank\" aria-hidden=\"true\">" + String(Rank) + "</span>" + AppOverviewView.Name(One)
                + "<span class=\"ovbig\">" + Count + "<span class=\"ovunit\"> messages</span></span>"
                + AppOverviewView.Spark(Per) + "</a></li>";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppOverviewView.Card");
            return "";
        }
    };

    /**
     * Messages per month, oldest first: [the 180 days before the last 30 / 6, (last 90 - last 30) / 2, last 30], rounded.
     * No numbers = three zeros. DOCUMENTED EXCEPTION: pure arithmetic.
     */
    public static Trend = (Act: AppActivity | null): number[] => {
        if (Act === null) { return [0, 0, 0]; }
        return [Math.round(Act.P180 / 6), Math.round(Math.max(0, Act.D90 - Act.D30) / 2), Act.D30];
    };

    /** The trend line: 64x20, the highest point at the top, an end dot. DOCUMENTED EXCEPTION: pure markup. */
    public static Spark = (Per: number[]): string => {
        const Most: number = Math.max(1, Per[0], Per[1], Per[2]);
        const Pts: string[] = [];
        let LastX: number = 0;
        let LastY: number = 0;
        for (let I: number = 0; I < Per.length; I++) {
            LastX = 4 + I * 28;
            LastY = Math.round((18 - (Per[I] / Most) * 15) * 10) / 10;
            Pts.push(String(LastX) + "," + String(LastY));
        }
        return "<svg class=\"ovspark\" viewBox=\"0 0 64 20\" width=\"64\" height=\"20\" aria-hidden=\"true\" focusable=\"false\">"
            + "<polyline points=\"" + Pts.join(" ") + "\" fill=\"none\" stroke-width=\"2\" stroke-linecap=\"round\" stroke-linejoin=\"round\"></polyline>"
            + "<circle cx=\"" + String(LastX) + "\" cy=\"" + String(LastY) + "\" r=\"2.5\"></circle></svg>";
    };

    /** "Gone quiet": who, and their usual level against how long it has been. */
    private static QuietSection = (Index: AppIndex): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Rows: AppOverviewEntry[] = AppOverview.Quiet(Index);
            const Out: string[] = ["<section class=\"ovsec ovquiet\"><div class=\"ovhead\"><h2" + AppOverviewView.Tip + ">Gone quiet</h2></div>"];
            if (Rows.length === 0) {
                Out.push("<p class=\"none\">Nobody you usually talk to often has gone quiet.</p></section>");
                return Out.join("");
            }
            Out.push("<ul class=\"ovlist\">");
            for (const One of Rows) {
                const Act: AppActivity | null = One.Contact.Act;
                let Why: string = "";
                if (Act !== null) { Why = AppActivityRules.QuietWhy(Act, One.Contact.DaysSinceLast); }
                Out.push("<li><a href=\"" + AppOverviewView.Link(One) + "\">" + AppOverviewView.Name(One)
                    + "<span class=\"ovwhy\">" + kHTMLCore.EscapeHtml(Why) + "</span></a></li>");
            }
            Out.push("</ul></section>");
            return Out.join("");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppOverviewView.QuietSection");
            return "";
        }
    };

    /** "Replies you owe": who, and how long their last message has waited. */
    private static OwedSection = (Index: AppIndex, Today: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Rows: AppOverviewEntry[] = AppOverview.Owed(Index, Today);
            const Out: string[] = ["<section class=\"ovsec ovowed\"><div class=\"ovhead\"><h2" + AppOverviewView.Tip + ">Replies you owe</h2></div>"];
            if (Rows.length === 0) {
                Out.push("<p class=\"none\">No replies owed.</p></section>");
                return Out.join("");
            }
            Out.push("<ul class=\"ovlist\">");
            for (const One of Rows) {
                let Wait: string = "waiting " + AppViews.Ago(One.Count);
                if (One.Count <= 0) { Wait = "since today"; }
                Out.push("<li><a href=\"" + AppOverviewView.Link(One) + "\">" + AppOverviewView.Name(One)
                    + "<span class=\"ovwhy\">" + kHTMLCore.EscapeHtml(Wait) + "</span></a></li>");
            }
            Out.push("</ul></section>");
            return Out.join("");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppOverviewView.OwedSection");
            return "";
        }
    };

    /** Every contact's disc and name; a missing picture leaves their initials. */
    private static Name = (One: AppOverviewEntry): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            return "<span class=\"ovname\">" + AppAvatarRules.Disc(One.Contact.Title, One.Contact.Avatar)
                + "<span class=\"ovt\">" + kHTMLCore.EscapeHtml(One.Contact.Title) + "</span></span>";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppOverviewView.Name");
            return "";
        }
    };

    /** The chat's route, escaped for an attribute. */
    private static Link = (One: AppOverviewEntry): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            return kHTMLCore.EscapeAttr("#c/" + encodeURIComponent(One.Contact.Href));
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppOverviewView.Link");
            return "";
        }
    };
}
